import React, { useEffect, useState } from 'react';
import SearchIcon from '@mui/icons-material/Search';
import DownloadIcon from '@mui/icons-material/Download';
import VisibilityIcon from '@mui/icons-material/Visibility';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import API from '../../services/api';
import { DateRangePicker } from 'materialui-daterange-picker';
import { TextField } from '@material-ui/core';

const SuperAdminInsights = () => {
  const [from, setFrom] = useState(null); // Updated to null for better consistency
  const [to, setTo] = useState(null);     // Updated to null for better consistency
  const [dateRangeVisible, setDateRangeVisible] = useState(false);
  const [dateRange, setDateRange] = useState({ startDate: null, endDate: null });
  const [search, setSearch] = useState('');
  const [users, setUsers] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [view, setView] = useState('users');
  const [userPagination, setUserPagination] = useState({ page: 1, limit: 50, totalPages: 1, totalEntries: 0 });
  const [projectPagination, setProjectPagination] = useState({ page: 1, limit: 50, totalPages: 1, totalEntries: 0 });

  // Log state changes for debugging
  useEffect(() => {
    // console.log('State updated - search:', search, 'from:', from, 'to:', to, 'view:', view);
  }, [search, from, to, view]);

  // Fetch users by default on component mount
  useEffect(() => {
    fetchUsers({ page: 1, searchText: '', from: null, to: null });
  }, []);

  const fetchUsers = async (params = {}) => {
    // console.log('Fetching users with params:', params);
    setLoading(true);
    setError(null);
    try {
      const queryParams = {
        page: params.page ?? userPagination.page,
        limit: userPagination.limit,
        searchText: params.searchText ?? search,
        userType: [],
      };

      const fromVal = params.from ?? from;
      if (fromVal) queryParams.from = Number(fromVal);

      const toVal = params.to ?? to;
      if (toVal) queryParams.to = Number(toVal);

      const res = await API.getAllUsers(queryParams);
      // console.log('Users fetched:', res.data); // Existing log
      // console.log('Full response:', res); // Added for deeper debugging
      setUsers(res.data || []);
      setUserPagination(prev => ({
        ...prev,
        totalPages: res.pagination?.totalPages || 1,
        totalEntries: res.pagination?.total || 0,
      }));
    } catch (e) {
      console.error('Error fetching users:', e);
      console.error('Error details:', e.response?.data || e.message); // Added for better error info
      setError('Failed to load users. Please try again.');
      setUsers([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchProjects = async (params = {}) => {
    // console.log('Fetching projects with params:', params);
    setLoading(true);
    setError(null);
    try {
      const queryParams = {
        page: params.page ?? projectPagination.page,
        limit: projectPagination.limit,
        searchText: params.searchText ?? search,
      };

      const fromVal = params.from ?? from;
      if (fromVal) queryParams.from = Number(fromVal);

      const toVal = params.to ?? to;
      if (toVal) queryParams.to = Number(toVal);

      const res = await API.getAllProjects(queryParams);
      // console.log('Projects fetched:', res.data); // Existing log
      // console.log('Full response:', res); // Added for deeper debugging
      setProjects(res.data || []);
      setProjectPagination(prev => ({
        ...prev,
        totalPages: res.pagination?.totalPages || 1,
        totalEntries: res.pagination?.total || 0,
      }));
    } catch (e) {
      console.error('Error fetching projects:', e);
      console.error('Error details:', e.response?.data || e.message); // Added for better error info
      setError('Failed to load projects. Please try again.');
      setProjects([]);
    } finally {
      setLoading(false);
    }
  };

  // Handle search
  const handleSearch = (e) => {
    const searchValue = e.target.value;
    setSearch(searchValue);
    if (searchValue === '' || searchValue.length > 1) {
      if (view === 'users') {
        fetchUsers({ searchText: searchValue });
      } else if (view === 'projects') {
        fetchProjects({ searchText: searchValue });
      }
    }
  };

  // Search on enter
  const handleSearchKeyDown = (e) => {
    if (e.key === 'Enter') {
      if (view === 'users') fetchUsers({ searchText: search });
      else if (view === 'projects') fetchProjects({ searchText: search });
    }
  };

  // Date range picker logic
  const toggleDateRange = () => setDateRangeVisible(!dateRangeVisible);
  const setDateRangeValues = (range) => {
    const fromVal = range.startDate ? new Date(range.startDate).getTime() : null;
    const toVal = range.endDate ? new Date(range.endDate).getTime() : null;
    setDateRange({ startDate: range.startDate, endDate: range.endDate });
    setFrom(fromVal);
    setTo(toVal);
    setDateRangeVisible(false);
    if (fromVal && toVal) {
      if (view === 'users') {
        fetchUsers({ from: fromVal, to: toVal });
      } else if (view === 'projects') {
        fetchProjects({ from: fromVal, to: toVal });
      }
    }
  };

  // Clear filters and fetch unfiltered data (updated with more logging)
  const handleClearFilter = () => {
    // console.log('Clear Filter clicked! Current view:', view);
    // console.log('Before reset - search:', search, 'from:', from, 'to:', to);
    setSearch('');
    setFrom(null); // Updated to null
    setTo(null);   // Updated to null
    setDateRange({ startDate: null, endDate: null });
    setError(null);
    // console.log('After reset - search:', search, 'from:', from, 'to:', to); // Note: May not show immediately due to async state
    if (view === 'users') {
      setUsers([]); // Clear users immediately
      setUserPagination(prev => ({ ...prev, page: 1 }));
      fetchUsers({ page: 1, searchText: '', from: null, to: null });
    } else if (view === 'projects') {
      setProjects([]); // Clear projects immediately
      setProjectPagination(prev => ({ ...prev, page: 1 }));
      fetchProjects({ page: 1, searchText: '', from: null, to: null });
    }
  };

  // Toggle view
  const handleView = (type) => {
    // console.log('Switching to view:', type);
    if (type === view) {
      // Refetch with reset if clicking active view
      if (type === 'users') {
        setUsers([]);
        setUserPagination(prev => ({ ...prev, page: 1 }));
        fetchUsers({ page: 1, searchText: '', from: null, to: null });
      } else if (type === 'projects') {
        setProjects([]);
        setProjectPagination(prev => ({ ...prev, page: 1 }));
        fetchProjects({ page: 1, searchText: '', from: null, to: null });
      }
      return;
    }

    setLoading(true);
    setError(null);
    setSearch('');
    setFrom(null); // Updated to null
    setTo(null);   // Updated to null
    setDateRange({ startDate: null, endDate: null });
    setView(type);
    if (type === 'users') {
      setUsers([]);
      setUserPagination(prev => ({ ...prev, page: 1 }));
      fetchUsers({ page: 1, searchText: '', from: null, to: null });
    } else if (type === 'projects') {
      setProjects([]);
      setProjectPagination(prev => ({ ...prev, page: 1 }));
      fetchProjects({ page: 1, searchText: '', from: null, to: null });
    }
  };

  // Helper function to properly format a CSV cell
  const formatCsvCell = (value) => {
    if (value === null || value === undefined) return '""';
    const str = String(value);
    return `"${str.replace(/"/g, '""')}"`;
  };

  // Download current data as CSV
  const downloadCurrentData = () => {
    let csvContent = '';
    if (view === 'users') {
      const headers = [
        'No.',
        'User Name',
        'Email',
        'Downloaded Track',
        'User Type',
        'Project Count',
        'License Count',
        'Channel',
        'Created On',
      ].map(formatCsvCell).join(',');

      const rows = users.map((user, idx) => [
        (userPagination.page - 1) * userPagination.limit + idx + 1,
        `${user.first_name || ''} ${user.last_name || ''}`,
        user.email || '',
        user.downloadedTracksIndex?.length || 0,
        user.userType || '',
        user.projectCount || 0,
        user.licenseCount || 0,
        user.channel || '',
        new Date(user.createdAt).toLocaleDateString() || '',
      ].map(formatCsvCell).join(','));

      csvContent = [headers, ...rows].join('\n');
    } else if (view === 'projects') {
      const headers = [
        'Title',
        'Created By',
        'Created By Email',
        'Track',
        'Track Duration',
        'Created On',
        'User Type',
      ].map(formatCsvCell).join(',');

      const rows = projects.map((project) => [
        project.title || '',
        project.userName || '',
        project.emailId || '',
        project.trackIds?.map(t => t.copyrightInfo?.split('\n')[0]?.replace('DisplayTitle: ', '') || '').join('; ') || '',
        project.trackIds?.map(t => t.copyrightInfo?.match(/Length: ([^\n]+)/)?.[1] || '').join('; ') || '',
        new Date(project.createdAt).toLocaleDateString() || '',
        project.userType || '',
      ].map(formatCsvCell).join(','));

      csvContent = [headers, ...rows].join('\n');
    }

    if (!csvContent) return;

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = view === 'users' ? 'users.csv' : 'projects.csv';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // Function to determine email class based on length
  const getEmailClass = (email) => {
    const referenceEmail = 'mathukuttyxavierhere@gmail.com';
    return email && email.length >= referenceEmail.length ? 'px-4 py-2' : 'px-4 py-2 max-w-[200px] overflow-wrap break-word';
  };

  return (
    <div className="">
      <div className="bg-white rounded-lg shadow p-5 mb-8">
        <div className="flex flex-col gap-y-2 justify-between mb-4">
          <span className="flex items-center gap-2 text-lg font-semibold text-gray-700">
            <SearchIcon fontSize="small" />Insights
          </span>
          <div className="grid grid-cols-2 lg:grid-cols-8 gap-3">
            {view === 'users' && (
              <div className="flex flex-col gap-2 col-span-2 lg:col-span-2">
                <label className="text-sm font-medium">User(s):</label>
                <input
                  type="text"
                  placeholder="Search user..."
                  className="py-2.5 px-4 border border-gray-200 rounded-md text-sm bg-gray-50 text-gray-700 w-full focus:outline-none focus:ring-2 focus:ring-blue-500"
                  value={search}
                  onChange={handleSearch}
                  onKeyDown={handleSearchKeyDown}
                />
              </div>
            )}
            {view === 'projects' && (
              <div className="flex flex-col gap-2 col-span-2 lg:col-span-2">
                <label className="text-sm font-medium">Project(s):</label>
                <input
                  type="text"
                  placeholder="Search project..."
                  className="py-2.5 px-4 border border-gray-200 rounded-md text-sm bg-gray-50 text-gray-700 w-full focus:outline-none focus:ring-2 focus:ring-blue-500"
                  value={search}
                  onChange={handleSearch}
                  onKeyDown={handleSearchKeyDown}
                />
              </div>
            )}
            <div className="flex flex-col gap-2 col-span-1 lg:col-span-1">
              <label className="text-sm font-medium">From:</label>
              <TextField
                type="text"
                value={from ? new Date(Number(from)).toLocaleDateString('en-GB') : ''}
                onClick={toggleDateRange}
                readOnly
                variant="outlined"
                size="small"
                className="bg-white w-full cursor-pointer"
                placeholder="DD/MM/YYYY"
                onFocus={(e) => e.preventDefault()}
                InputProps={{
                  endAdornment: (
                    <CalendarTodayIcon style={{ color: 'gray', cursor: 'pointer' }} onClick={toggleDateRange} />
                  ),
                }}
              />
            </div>
            <div className="flex flex-col gap-2 col-span-1 lg:col-span-1">
              <label className="text-sm font-medium">To:</label>
              <TextField
                type="text"
                value={to ? new Date(Number(to)).toLocaleDateString('en-GB') : ''}
                onClick={toggleDateRange}
                readOnly
                variant="outlined"
                size="small"
                className="bg-white w-full cursor-pointer"
                placeholder="DD/MM/YYYY"
                onFocus={(e) => e.preventDefault()}
                InputProps={{
                  endAdornment: (
                    <CalendarTodayIcon style={{ color: 'gray', cursor: 'pointer' }} onClick={toggleDateRange} />
                  ),
                }}
              />
            </div>
            <div className="col-span-1 lg:col-span-1 flex items-end">
              <button
                className={`flex items-center justify-center gap-2 bg-gray-200 text-gray-700 rounded-lg font-medium shadow-sm hover:shadow-md hover:bg-gray-300 hover:scale-105 transition transform duration-200 disabled:opacity-50 disabled:cursor-not-allowed w-full`}
                style={{ paddingTop: '16px', paddingBottom: '16px', paddingLeft: '12px', paddingRight: '12px', height: '44px' }}
                onClick={handleClearFilter}
                // Disabled prop removed for testing; add back if needed: disabled={!search && !from && !to}
              >
                <span className="text-xs lg:text-sm">Clear Filter</span>
              </button>
            </div>
            <div className="col-span-1 lg:col-span-1 flex items-end">
              <button
                className="flex items-center justify-center gap-2 bg-blue-600 text-white rounded-lg font-medium shadow-sm hover:shadow-md hover:bg-blue-700 hover:scale-105 transition transform duration-200 w-full"
                style={{ paddingTop: '16px', paddingBottom: '16px', paddingLeft: '12px', paddingRight: '12px', height: '44px' }}
                onClick={downloadCurrentData}
                title="Export CSV"
              >
                <DownloadIcon fontSize="small" />
                <span className="text-xs lg:text-sm">Export</span>
              </button>
            </div>
            <div className="col-span-1 lg:col-span-1 flex items-end">
              <button
                className={`flex items-center justify-center gap-2 bg-teal-300 text-gray-900 rounded-lg font-semibold shadow-sm hover:shadow-md hover:bg-teal-400 hover:scale-105 transition transform duration-200 w-full ${view === 'projects' ? 'ring-2 ring-teal-500' : ''}`}
                style={{ paddingTop: '16px', paddingBottom: '16px', paddingLeft: '12px', paddingRight: '12px', height: '44px' }}
                onClick={() => handleView('projects')}
              >
                <VisibilityIcon fontSize="small" />
                <span className="text-xs lg:text-sm">Projects</span>
              </button>
            </div>
            <div className="col-span-1 lg:col-span-1 flex items-end">
              <button
                className={`flex items-center justify-center gap-2 bg-indigo-500 text-white rounded-lg font-medium shadow-sm hover:shadow-md hover:bg-indigo-600 hover:scale-105 transition transform duration-200 w-full ${view === 'users' ? 'ring-2 ring-indigo-400' : ''}`}
                style={{ paddingTop: '16px', paddingBottom: '16px', paddingLeft: '12px', paddingRight: '12px', height: '44px' }}
                onClick={() => handleView('users')}
              >
                <AccountCircleIcon fontSize="small" />
                <span className="text-xs lg:text-sm">Account</span>
              </button>
            </div>
          </div>
        </div>

        {view === 'users' && (
          <div className="overflow-x-auto mb-8">
            <table className="min-w-full bg-white rounded-lg shadow text-sm">
              <thead>
                <tr>
                  <th className="bg-gray-700 text-white font-semibold px-4 py-3 text-left">No.</th>
                  <th className="bg-gray-700 text-white font-semibold px-4 py-3 text-left">User Name</th>
                  <th className="bg-gray-700 text-white font-semibold px-4 py-3 text-left">Email</th>
                  <th className="bg-gray-700 text-white font-semibold px-4 py-3 text-left">Downloaded Track</th>
                  <th className="bg-gray-700 text-white font-semibold px-4 py-3 text-left">User Type</th>
                  <th className="bg-gray-700 text-white font-semibold px-4 py-3 text-left">Project Count</th>
                  <th className="bg-gray-700 text-white font-semibold px-4 py-3 text-left">License Count</th>
                  <th className="bg-gray-700 text-white font-semibold px-4 py-3 text-left">Channel</th>
                  <th className="bg-gray-700 text-white font-semibold px-4 py-3 text-left">Created On</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={9} className="text-center py-4">Loading...</td></tr>
                ) : error ? (
                  <tr><td colSpan={9} className="text-center py-4 text-red-600">{error}</td></tr>
                ) : users.length === 0 ? (
                  <tr><td colSpan={9} className="text-center py-4">No users found.</td></tr>
                ) : (
                  users.map((user, idx) => (
                    <tr key={user._id || idx} className="hover:bg-gray-50"> {/* Added fallback for key if _id is null */}
                      <td className="px-4 py-2">{(userPagination.page - 1) * userPagination.limit + idx + 1}</td>
                      <td className="px-4 py-2">{user.first_name || ''} {user.last_name || ''}</td> {/* Added null checks */}
                      <td className={getEmailClass(user.email)}>{user.email || ''}</td>
                      <td className="px-4 py-2">{user.downloadedTracksIndex?.length || 0}</td>
                      <td className="px-4 py-2">{user.userType || ''}</td>
                      <td className="px-4 py-2">{user.projectCount || 0}</td>
                      <td className="px-4 py-2">{user.licenseCount || 0}</td>
                      <td className="px-4 py-2">{user.channel || ''}</td>
                      <td className="px-4 py-2">{user.createdAt ? new Date(user.createdAt).toLocaleDateString() : ''}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
            {userPagination.totalPages > 1 && (
              <div className="flex flex-col sm:flex-row items-center justify-between px-4 py-3 border-t border-gray-200 bg-gray-50 gap-4">
                <div className="text-sm text-gray-700">
                  Showing {Math.min((userPagination.page - 1) * userPagination.limit + 1, userPagination.totalEntries)} to {Math.min(userPagination.page * userPagination.limit, userPagination.totalEntries)} of {userPagination.totalEntries} entries (Total Pages: {userPagination.totalPages})
                </div>
                <div className="flex items-center gap-2">
                  <button
                    className="px-3 py-1 rounded-md border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-sm"
                    onClick={() => {
                      setUserPagination(prev => ({ ...prev, page: Math.max(1, prev.page - 1) }));
                      fetchUsers({ page: Math.max(1, userPagination.page - 1) });
                    }}
                    disabled={userPagination.page === 1}
                  >Previous</button>
                  <div className="flex gap-1">
                    {Array.from({ length: Math.min(5, userPagination.totalPages) }, (_, i) => {
                      let pageNum;
                      if (userPagination.totalPages <= 5) {
                        pageNum = i + 1;
                      } else if (userPagination.page <= 3) {
                        pageNum = i + 1;
                      } else if (userPagination.page >= userPagination.totalPages - 2) {
                        pageNum = userPagination.totalPages - 4 + i;
                      } else {
                        pageNum = userPagination.page - 2 + i;
                      }
                      return (
                        <button
                          key={pageNum}
                          className={`px-3 py-1 rounded-md border transition-colors text-sm ${
                            userPagination.page === pageNum
                              ? 'bg-blue-600 text-white border-blue-600'
                              : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                          }`}
                          onClick={() => {
                            setUserPagination(prev => ({ ...prev, page: pageNum }));
                            fetchUsers({ page: pageNum });
                          }}
                        >
                          {pageNum}
                        </button>
                      );
                    })}
                  </div>
                  <button
                    className="px-3 py-1 rounded-md border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-sm"
                    onClick={() => {
                      setUserPagination(prev => ({ ...prev, page: Math.min(userPagination.totalPages, prev.page + 1) }));
                      fetchUsers({ page: Math.min(userPagination.totalPages, userPagination.page + 1) });
                    }}
                    disabled={userPagination.page === userPagination.totalPages}
                  >Next</button>
                </div>
              </div>
            )}
          </div>
        )}
        {dateRangeVisible && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
            <div className="bg-white rounded-2xl shadow-2xl max-w-full max-h-full overflow-auto">
              <DateRangePicker
                open={dateRangeVisible}
                toggle={toggleDateRange}
                onChange={setDateRangeValues}
              />
            </div>
          </div>
        )}
        {view === 'projects' && (
          <div className="overflow-x-auto">
            <table className="min-w-full bg-white rounded-lg shadow text-sm">
              <thead>
                <tr>
                  <th className="bg-gray-700 text-white font-semibold px-4 py-3 text-left">Title</th>
                  <th className="bg-gray-700 text-white font-semibold px-4 py-3 text-left">Created By</th>
                  <th className="bg-gray-700 text-white font-semibold px-4 py-3 text-left">Created By Email</th>
                  <th className="bg-gray-700 text-white font-semibold px-4 py-3 text-left">Track</th>
                  <th className="bg-gray-700 text-white font-semibold px-4 py-3 text-left">Track Duration</th>
                  <th className="bg-gray-700 text-white font-semibold px-4 py-3 text-left">Created On</th>
                  <th className="bg-gray-700 text-white font-semibold px-4 py-3 text-left">User Type</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={7} className="text-center py-4">Loading...</td></tr>
                ) : error ? (
                  <tr><td colSpan={7} className="text-center py-4 text-red-600">{error}</td></tr>
                ) : projects.length === 0 ? (
                  <tr><td colSpan={7} className="text-center py-4">No projects found.</td></tr>
                ) : (
                  projects.map((project, idx) => (
                    <tr key={project._id || idx} className="hover:bg-gray-50"> {/* Added fallback for key if _id is null */}
                      <td className="px-4 py-2">{project.title || ''}</td>
                      <td className="px-4 py-2">{project.userName || ''}</td>
                      <td className={getEmailClass(project.emailId)}>{project.emailId || ''}</td>
                      <td className="px-4 py-2">
                        <div className="max-h-16 overflow-y-auto whitespace-pre-line">
                          {project.trackIds?.map(t => t.copyrightInfo?.split('\n')[0]?.replace('DisplayTitle: ', '') || '').join(', ') || ''}
                        </div>
                      </td>
                      <td className="px-4 py-2">
                        <div className="max-h-16 overflow-y-auto whitespace-pre-line">
                          {project.trackIds?.map(t => t.copyrightInfo?.match(/Length: ([^\n]+)/)?.[1] || '').join(', ') || ''}
                        </div>
                      </td>
                      <td className="px-4 py-2">{project.createdAt ? new Date(project.createdAt).toLocaleDateString() : ''}</td>
                      <td className="px-4 py-2">{project.userType || ''}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
            {projectPagination.totalPages > 1 && (
              <div className="flex flex-col sm:flex-row items-center justify-between px-4 py-3 border-t border-gray-200 bg-gray-50 gap-4">
                <div className="text-sm text-gray-700">
                  Showing {Math.min((projectPagination.page - 1) * projectPagination.limit + 1, projectPagination.totalEntries)} to {Math.min(projectPagination.page * projectPagination.limit, projectPagination.totalEntries)} of {projectPagination.totalEntries} entries (Total Pages: {projectPagination.totalPages})
                </div>
                <div className="flex items-center gap-2">
                  <button
                    className="px-3 py-1 rounded-md border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-sm"
                    onClick={() => {
                      setProjectPagination(prev => ({ ...prev, page: Math.max(1, prev.page - 1) }));
                      fetchProjects({ page: Math.max(1, projectPagination.page - 1) });
                    }}
                    disabled={projectPagination.page === 1}
                  >Previous</button>
                  <div className="flex gap-1">
                    {Array.from({ length: Math.min(5, projectPagination.totalPages) }, (_, i) => {
                      let pageNum;
                      if (projectPagination.totalPages <= 5) {
                        pageNum = i + 1;
                      } else if (projectPagination.page <= 3) {
                        pageNum = i + 1;
                      } else if (projectPagination.page >= projectPagination.totalPages - 2) {
                        pageNum = projectPagination.totalPages - 4 + i;
                      } else {
                        pageNum = projectPagination.page - 2 + i;
                      }
                      return (
                        <button
                          key={pageNum}
                          className={`px-3 py-1 rounded-md border transition-colors text-sm ${
                            projectPagination.page === pageNum
                              ? 'bg-blue-600 text-white border-blue-600'
                              : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                          }`}
                          onClick={() => {
                            setProjectPagination(prev => ({ ...prev, page: pageNum }));
                            fetchProjects({ page: pageNum });
                          }}
                        >
                          {pageNum}
                        </button>
                      );
                    })}
                  </div>
                  <button
                    className="px-3 py-1 rounded-md border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-sm"
                    onClick={() => {
                      setProjectPagination(prev => ({ ...prev, page: Math.min(projectPagination.totalPages, prev.page + 1) }));
                      fetchProjects({ page: Math.min(projectPagination.totalPages, projectPagination.page + 1) });
                    }}
                    disabled={projectPagination.page === projectPagination.totalPages}
                  >Next</button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default SuperAdminInsights;
